import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { groupApi, mastersApi, type Group, type GroupDetail, type LedgerRow, type Nature } from "../lib/api";
import { fmtAmount } from "../lib/money";
import { Link } from "react-router-dom";
import { useCompany } from "./reference";
import { descendants, groupTree, NATURE } from "./groups";
import { Combobox } from "../ui/Combobox";
import { formKeys } from "../ui/form";
import { errText } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { useShortcuts } from "../lib/useShortcuts";
import { toUrl } from "../shell/routes";

type Draft = { id?: string; name: string; number: string; parent_id: string | null; nature: Nature; affects_gross_profit: boolean };

// Chart of accounts: the numbered tree of groups on the left, the selected group (or a new one) on the right. Alt+N new, Alt+D delete.
export function Groups() {
  const { company, ref, reloadRef } = useCompany();
  const groups = ref?.groups ?? [];
  const rows = useMemo(() => groupTree(groups), [groups]);
  const [sel, setSel] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const list = useRef<HTMLUListElement>(null);
  const canEdit = ref?.can_edit ?? false;
  const selected = rows[sel]?.group;
  const codes = ref?.company.account_codes ?? false;
  const [showAccounts, setShowAccounts] = useState(() => { try { return localStorage.getItem("books.coa.accounts") !== "off"; } catch { return true; } });
  const [ledgers, setLedgers] = useState<LedgerRow[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const loadLedgers = () => mastersApi.ledgers(company.id).then(setLedgers).catch(() => setLedgers([]));
  useEffect(() => { void loadLedgers(); }, [company.id]);
  const byGroup = useMemo(() => {
    const m = new Map<string, LedgerRow[]>();
    for (const l of ledgers) if (l.active && !l.is_party) m.set(l.group_id, [...(m.get(l.group_id) ?? []), l]);
    for (const v of m.values()) v.sort((a, b) => (codes ? (a.number ?? "").localeCompare(b.number ?? "") : 0) || a.name.localeCompare(b.name));
    return m;
  }, [ledgers, codes]);
  const toggleAccounts = (on: boolean) => { setShowAccounts(on); try { localStorage.setItem("books.coa.accounts", on ? "on" : "off"); } catch { /* fine */ } };
  const setCodes = async (on: boolean) => {
    setErr(null);
    try { await mastersApi.setAccountCodes(company.id, on); await reloadRef(); } catch (x) { setErr(errText(x)); }
  };
  const addStandard = async () => {
    setBusy(true); setErr(null); setNote(null);
    try {
      const n = await mastersApi.addStandardAccounts(company.id);
      await reloadRef(); await loadLedgers();
      setNote(n ? `${n} standard account${n > 1 ? "s" : ""} added.` : "The standard accounts are all here already.");
    } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };

  const edit = (g: Group) => { setErr(null); setConfirmDelete(false); setDraft({ id: g.id, name: g.name, number: g.number ?? "", parent_id: g.parent_id, nature: g.nature, affects_gross_profit: g.affects_gross_profit }); };
  const create = () => { if (!canEdit) return; setErr(null); setConfirmDelete(false); setDraft({ name: "", number: "", parent_id: selected?.id ?? null, nature: selected?.nature ?? "A", affects_gross_profit: false }); };
  const close = () => { setDraft(null); setErr(null); setConfirmDelete(false); setTimeout(() => list.current?.focus(), 0); };

  const [focusId, setFocusId] = useState<string | null>(null);
  useEffect(() => { list.current?.focus(); void reloadRef(); }, []);   // fresh ledger counts
  useEffect(() => {
    if (!focusId) return;
    const i = rows.findIndex((r) => r.group.id === focusId);
    if (i >= 0) { setSel(i); setFocusId(null); list.current?.focus(); }
  }, [rows, focusId]);
  useShortcuts([
    { keys: "Alt+N", run: create },
    { keys: "Alt+D", run: () => { if (draft?.id && canEdit) setConfirmDelete(true); } },
  ]);

  if (!ref) return <section className="page"><PageHead crumb="Accounting" title="Chart of accounts" code="AC10" /><Loading /></section>;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setBusy(true); setErr(null);
    try {
      const body: Partial<Group> = { id: draft.id, name: draft.name, number: draft.number.trim() || null, parent_id: draft.parent_id };
      if (!draft.parent_id) { body.nature = draft.nature; body.affects_gross_profit = draft.affects_gross_profit; }
      const id = await mastersApi.saveGroup(company.id, body);
      await reloadRef();
      setDraft(null);
      setFocusId(id);
    } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!draft?.id) return;
    setBusy(true);
    try { await mastersApi.deleteGroup(company.id, draft.id); await reloadRef(); close(); setSel((s) => Math.max(0, s - 1)); }
    catch (x) { setErr(errText(x)); setConfirmDelete(false); } finally { setBusy(false); }
  };

  const orig = draft?.id ? groups.find((g) => g.id === draft.id) : undefined;
  const blocked = draft?.id ? descendants(groups, draft.id) : new Set<string>();
  const parentOptions = rows.filter((r) => !blocked.has(r.group.id))
    .map((r) => ({ value: r.group.id, label: r.group.name, hint: NATURE[r.group.nature] }));

  return (
    <section className="page">
      <PageHead crumb="Accounting" title="Chart of accounts" code="AC10">
        {canEdit && <button onClick={() => void addStandard()} disabled={busy} title="Adds Sales, Purchases, Cost of Goods Sold, Salaries, Rent and the other usual accounts that aren't here yet">Add standard accounts</button>}
        {canEdit && <button onClick={create}>New group <kbd>Alt+N</kbd></button>}
      </PageHead>
      <div className="toolbar">
        <label className="check"><input type="checkbox" checked={showAccounts} onChange={(e) => toggleAccounts(e.target.checked)} /> Show accounts</label>
        <label className="check" title="Show and enter a number for every group and account"><input type="checkbox" checked={codes} disabled={!canEdit} onChange={(e) => void setCodes(e.target.checked)} /> Account codes</label>
        <span className="muted small">{ledgers.filter((l) => l.active && !l.is_party).length} accounts · customers and vendors are under Sales and Purchases</span>
      </div>
      {note && <p className="msg ok" role="status">{note}</p>}
      {!draft && err && <ErrorNote text={err} />}
      <div className="split">
        <ul ref={list} className="list tree" role="listbox" tabIndex={0} aria-label="Groups"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, rows.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
            else if (e.key === "Enter" && selected) { e.preventDefault(); edit(selected); }
          }}>
          {rows.map((r, i) => [
            <li key={r.group.id} role="option" aria-selected={i === sel} className={i === sel ? "sel" : ""}
                style={{ paddingLeft: `${0.8 + r.depth * 1.2}rem` }} onClick={() => { setSel(i); edit(r.group); }}
                ref={(el) => { if (i === sel && el) el.scrollIntoView({ block: "nearest" }); }}>
              {codes && <span className="acct-no">{r.group.number ?? ""}</span>}<span className={`grow${r.depth === 0 ? " strong" : ""}`}>{r.group.name}</span>
              <span className="muted small">{!showAccounts && r.group.ledgers ? `${r.group.ledgers} account${r.group.ledgers > 1 ? "s" : ""}` : ""}</span>
            </li>,
            ...(showAccounts ? (byGroup.get(r.group.id) ?? []).map((l) => (
              <li key={l.id} className="acct" style={{ paddingLeft: `${0.8 + (r.depth + 1) * 1.2}rem` }}>
                {codes && <span className="acct-no">{l.number ?? ""}</span>}
                <Link className="grow" to={toUrl(`masters/ledgers/${l.id}`)} tabIndex={-1}>{l.name}</Link>
              </li>)) : []),
          ])}
        </ul>
        <div className="stack-col">
        {draft && (
          <form className="card panel" onSubmit={save} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); } else formKeys(e); }}>
            <h2>{draft.id ? (orig?.is_predefined ? `${orig.name} (standard)` : "Group") : "New group"}</h2>
            <label>Name<input autoFocus required value={draft.name} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            {codes && <label>Number <span className="muted small">(3 to 8 digits, optional)</span><input inputMode="numeric" maxLength={8} className="short" value={draft.number} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, number: e.target.value.replace(/\D/g, "") })} /></label>}
            <label>Under
              <Combobox options={parentOptions} value={draft.parent_id} allowEmpty emptyLabel="Top level"
                disabled={!canEdit || orig?.is_predefined}
                onChange={(v) => setDraft({ ...draft, parent_id: v, nature: v ? groups.find((g) => g.id === v)!.nature : draft.nature })} />
            </label>
            {!draft.parent_id ? (
              <>
                <label>Nature
                  <select value={draft.nature} disabled={!canEdit || orig?.is_predefined} onChange={(e) => setDraft({ ...draft, nature: e.target.value as Nature, affects_gross_profit: false })}>
                    {(Object.keys(NATURE) as Nature[]).map((n) => <option key={n} value={n}>{NATURE[n]}</option>)}
                  </select>
                </label>
                {(draft.nature === "I" || draft.nature === "E") && (
                  <label className="check"><input type="checkbox" checked={draft.affects_gross_profit} disabled={!canEdit || orig?.is_predefined}
                    onChange={(e) => setDraft({ ...draft, affects_gross_profit: e.target.checked })} /> Affects gross profit (trading account)</label>
                )}
              </>
            ) : <p className="muted small">Nature: {NATURE[draft.nature]} (from the group above)</p>}
            {orig?.is_predefined && <p className="muted small">Standard groups can be renamed and renumbered, but not moved or deleted: the statements are built on them.</p>}
            <ErrorNote text={err} />
            {confirmDelete ? (
              <div className="confirm" role="alert">Delete “{orig?.name}”?
                <button type="button" className="danger" autoFocus onClick={remove} disabled={busy}>Delete</button>
                <button type="button" onClick={() => setConfirmDelete(false)}>Keep</button></div>
            ) : canEdit && (
              <div className="row">
                <button className="primary" disabled={busy}>{busy ? "Saving…" : "Save"} <kbd>Ctrl+S</kbd></button>
                {draft.id && !orig?.is_predefined && <button type="button" className="link danger-link" onClick={() => setConfirmDelete(true)}>Delete <kbd>Alt+D</kbd></button>}
                <button type="button" className="link" onClick={close}>Cancel <kbd>Esc</kbd></button>
              </div>
            )}
          </form>
        )}
        {draft?.id && <GroupFacts id={draft.id} codes={codes} />}
        {!draft && <p className="muted panel-hint">Select a group and press Enter to open it{canEdit ? ", or Alt+N for a new one under it" : ""}.</p>}
        </div>
      </div>
    </section>
  );
}

// where a group shows in the statements, its balance today and its accounts
function GroupFacts({ id, codes }: { id: string; codes: boolean }) {
  const { company } = useCompany();
  const [d, setD] = useState<GroupDetail | null>(null);
  useEffect(() => { setD(null); groupApi.detail(company.id, id).then(setD).catch(() => setD(null)); }, [company.id, id]);
  if (!d) return null;
  return (
    <aside className="card panel group-facts">
      {d.balance != null && <p><span className="muted small">Balance today</span><br /><b>₹ {fmtAmount(Math.abs(d.balance))}</b>{d.balance < 0 ? " (on the other side)" : ""}</p>}
      {d.lines.length > 0 && <div><span className="muted small">Where it appears</span>
        <ul>{d.lines.map((l, i) => <li key={i}>{l.statement === "bs" ? "Balance sheet" : "Profit and loss"} › {l.section} › {l.label}</li>)}</ul></div>}
      <div><span className="muted small">Accounts directly in it ({d.accounts.length})</span>
        {d.accounts.length ? <ul className="accts">{d.accounts.slice(0, 12).map((a) => (
          <li key={a.id}>{codes && <><span className="mono muted">{a.number ?? ""}</span> </>}<Link to={toUrl(`masters/ledgers/${a.id}`)}>{a.name}</Link></li>))}
          {d.accounts.length > 12 && <li className="muted">and {d.accounts.length - 12} more</li>}</ul> : <p className="muted small">None yet.</p>}</div>
    </aside>
  );
}
